import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { IAlert } from '@d-orca/shared/alert-kinds';
import { useAlertActions, useAlerts } from '@d-orca/shared/client/hooks';
import { zonedDay } from '@d-orca/shared/client/logistics';
import type { INotificationItem } from '@interfaces/INotificationItem';
import { createInvestigationReport, reportRoute } from '@apis/investigationReports';
import { useFeatures } from '@/permissions/useFeatures';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { alertItem } from '../items/alertItem';

/**
 * The bell's alert items for the site — the person's alerts as the stream
 * keeps them (`useAlerts`), worded and routed, with what opening and
 * dismissing one does — and how many are unread.
 */
export function useAlertItems(siteId: string | null): { items: INotificationItem[]; unread: number } {
  const { t } = useTranslation();
  const features = useFeatures();
  const tz = useSelector(selectSiteTz);
  const alerts = useAlerts(siteId);
  const { markViewed, dismiss } = useAlertActions(siteId);

  // The daily report alert says the day's overview is ready: build it, as
  // Trusstor's app does on the same click, for the day it was raised on —
  // which needs the site's zone, so not before the site has one.
  const buildDailyOverview = useCallback(
    async (site: string, zone: string, alert: IAlert): Promise<string | null> => {
      try {
        const { reportId } = await createInvestigationReport(site, {
          investigationType: 'projectOverview',
          title: t('reports.projectOverview.title'),
          description: '',
          periodPickerMode: 'DATE',
          dateFrom: zonedDay(alert.at, zone),
        });
        return reportRoute(site, 'projectOverview', reportId);
      } catch {
        return null;
      }
    },
    [t],
  );

  if (!siteId) return { items: [], unread: 0 };
  const ctx = {
    siteId,
    t,
    can: (f: Parameters<typeof features.has>[0]) => features.has(f),
    buildDailyOverview: tz ? (alert: IAlert) => buildDailyOverview(siteId, tz, alert) : undefined,
    markViewed,
    dismiss,
  };
  return {
    items: alerts.map((alert) => alertItem(alert, ctx)),
    unread: alerts.filter((alert) => !alert.viewed).length,
  };
}
